/**
 * Block 14 «Раскрой» — единый источник нумерации/списка годных остатков.
 *
 * Один проход по всем листам результата собирает плоский список годных остатков
 * (тот же фильтр `isUsableOffcut` и тот же порядок «лист → freeRects», что рисует
 * карта) — чтобы номера на карте (`О1, О2…`) и в таблице «Список остатков»
 * СОВПАДАЛИ. Возвращает также `startBySheet[idx]` — сколько годных остатков на
 * листах ДО `idx`, чтобы `CutMapCanvas` (рисует один лист) знал глобальное смещение
 * нумерации для своих остатков (`offcutStart`).
 */

import type { OptimizationResult } from '@pomoshnik/shared';
import { isUsableOffcut } from './resultMetrics';

const MM2_PER_M2 = 1e6;

/** Строка таблицы «Список остатков». */
export interface OffcutRow {
  /** Глобальный 1-based номер (как `ОN` на карте). */
  number: number;
  /** Длина (длинная сторона), мм. */
  length: number;
  /** Ширина (короткая сторона), мм. */
  width: number;
  /** Площадь, м² (сырое число; округление — в UI). */
  areaM2: number;
}

/**
 * Плоский список годных остатков всех листов + смещение нумерации на каждый лист.
 * `threshold` — порог годного остатка (`settings.minOffcutSize ?? DEFAULT_MIN_OFFCUT_MM`).
 */
export function buildOffcutList(
  result: OptimizationResult,
  threshold: number,
): { rows: OffcutRow[]; startBySheet: number[] } {
  const rows: OffcutRow[] = [];
  const startBySheet: number[] = [];
  let n = 0;

  for (const sheet of result.sheets) {
    startBySheet.push(n); // годных остатков ДО этого листа
    for (const r of sheet.freeRects.filter((fr) => isUsableOffcut(fr, threshold))) {
      n += 1;
      const w = Math.round(r.w);
      const h = Math.round(r.h);
      rows.push({
        number: n,
        length: Math.max(w, h),
        width: Math.min(w, h),
        areaM2: (r.w * r.h) / MM2_PER_M2,
      });
    }
  }

  return { rows, startBySheet };
}
